import { PageSection, PageType } from '@libs/common-next';

export const SPECIES_PATHNAMES = new Set([
    '/dog',
    '/fish',
    '/cat',
    '/bird',
    '/reptile',
    '/small-pet',
    '/pet-parents',
    '/farm-animal',
]);

type pageType = {
    page_type: string;
    page_section?: string;
    page_sub_section?: string | null;
    pathname?: string;
};

export const formatPath = (pathname: string) => {
    if (pathname.startsWith('/')) {
        pathname = pathname.slice(1);
    }
    if (pathname.endsWith('/')) {
        pathname = pathname.slice(0, -1);
    }
    return pathname.split('/');
};

export const isSpeciesPage = (pathname: string) => {
    const mappedPath = formatPath(pathname);
    return SPECIES_PATHNAMES.has(`/${mappedPath[0]}`);
};

const mapHelpPage = (formattedPath: string[]): pageType => {
    const pageSubSection =
        formattedPath.length > 1 ? formattedPath.at(-1) : null;

    return {
        page_type: PageType.HELP,
        page_section: PageSection.HELP,
        page_sub_section: pageSubSection ?? PageSection.LANDING,
    };
};

const mapLearningCenterPage = (formattedPath: string[]): pageType => {
    const petPath = formattedPath[1];
    const articlePath = formattedPath[2];

    const articleOrSection = articlePath
        ? PageSection.LEARNING_CENTER_ARTICLE
        : PageSection.LEARNING_CENTER_SECTION;
    const pageSection = petPath
        ? articleOrSection
        : PageSection.LEARNING_CENTER;
    const pageSubSection =
        articlePath ?? petPath?.split('-')[0] ?? PageSection.LANDING;

    return {
        page_type: PageType.LEARNING_CENTER,
        page_section: pageSection,
        page_sub_section: pageSubSection,
    };
};

const mapStoresDirectoryPage = (formattedPath: string[]): pageType => {
    const currentSelectedPath = formattedPath.at(-1)?.replace('.html', '');
    const pageSubSection =
        currentSelectedPath !== 'us' && currentSelectedPath !== 'ca'
            ? currentSelectedPath
            : null;

    return {
        page_type: PageType.STORES_DIRECTORY,
        page_section: PageSection.STORE_DIRECTORY,
        page_sub_section: pageSubSection,
    };
};

const mapSpeciesPage = (
    formattedPath: string[],
    pageType: string
): pageType => {
    const pageSubSection = formattedPath.at(-1);
    const pageSection = formattedPath[0];

    return {
        page_type: formattedPath.length > 1 ? pageType : PageType.SPECIES,
        page_sub_section: formattedPath.length > 1 ? pageSubSection : null,
        page_section: pageSection,
    };
};

export const getPageSubsection = (pathname: string): string => {
    if (pathname.includes('/about-you')) {
        return 'edit-profile';
    }
    if (pathname.includes('/order-history')) {
        return 'order-history';
    }
    if (pathname.includes('/order-details')) {
        return 'order-details';
    }
    if (pathname.includes('/services-grooming')) {
        return 'appointments';
    }
    if (pathname.includes('/address-book')) {
        return 'address-list';
    }
    if (pathname.includes('/credit-cards')) {
        return 'payment';
    }
    if (pathname.includes('/auto-ship')) {
        return 'autoship';
    }
    if (pathname.includes('/med-cards')) {
        return 'digital med cards';
    }

    return '';
};

const mapStaticPages = (pathname: string): pageType | null => {
    const normalizePath = formatPath(pathname).join('');

    if (pathname === '/') {
        return { page_type: PageType.HOMEPAGE };
    }
    if (pathname.startsWith('/featured-brands')) {
        return { page_type: PageType.BRANDS };
    }
    if (pathname.startsWith('/sign-in')) {
        return { page_type: PageType.SIGN_IN, page_section: 'login' };
    }
    if (pathname.startsWith('/sale')) {
        return { page_type: PageType.DEALS };
    }
    if (pathname.startsWith('/reset-password')) {
        return { page_type: PageType.RESET_PASSWORD };
    }
    if (pathname.startsWith('/featured-shops')) {
        return { page_type: PageType.FEATURE };
    }
    if (pathname.startsWith('/create-account')) {
        return {
            page_type: PageType.CREATE_ACCOUNT,
            page_section: 'create account',
        };
    }
    if (pathname.startsWith(`/gift-cards`)) {
        return {
            page_type: PageType.FEATURE,
            page_section: PageSection.FEATURE,
            page_sub_section: null,
        };
    }
    // had to account for the trailing slash in /shop-by-brand or /shop-by-brand/ path
    if (normalizePath === 'shop-by-brand') {
        return {
            page_type: PageType.BRANDS,
            page_section: PageSection.SHOP_BY_BRAND,
            page_sub_section: PageSection.LANDING,
        };
    }
    if (pathname === '/account/contact') {
        return {
            page_type: PageType.HELP,
            page_section: PageSection.HELP_CENTER,
            page_sub_section: PageSection.HELP_CONTACT_US,
        };
    }
    if (pathname.startsWith('/store-locator')) {
        return {
            page_type: PageType.STORE_LOCATOR,
            page_section: PageSection.STORE_LOCATOR,
            page_sub_section: null,
        };
    }
    if (pathname.includes('/order-search')) {
        return {
            page_type: PageType.ORDER_SEARCH,
            page_section: PageSection.ORDER_SEARCH,
            page_sub_section: PageSection.ORDER_DETAILS,
        };
    }

    if (pathname.includes('/offers')) {
        return {
            page_type: PageType.TREATS_REWARDS,
            page_sub_section: 'offers page',
        };
    }

    if (pathname.startsWith('/treats-rewards')) {
        return {
            page_type: PageType.TREATS_REWARDS,
        };
    }

    if (pathname.startsWith('/account')) {
        return {
            page_type: PageType.ACCOUNT,
            page_section: PageType.ACCOUNT,
            page_sub_section: getPageSubsection(pathname),
        };
    }
    return null;
};

export const pageDataMapper = (
    pageType: string,
    pathname: string
): pageType => {
    const isNotFound = document.getElementsByClassName('error-not-found');
    if (isNotFound.length) {
        return {
            page_type: '404 error',
            page_sub_section: PageSection.LANDING,
            page_section: '404 error',
        };
    }

    const formattedPath = formatPath(pathname);
    const staticPage = mapStaticPages(pathname);

    if (staticPage) {
        return staticPage;
    }

    if (pathname.startsWith('/help')) {
        return mapHelpPage(formattedPath);
    }
    if (pathname.startsWith('/learning-center')) {
        return mapLearningCenterPage(formattedPath);
    }
    if (pathname.startsWith('/stores')) {
        return mapStoresDirectoryPage(formattedPath);
    }
    if (isSpeciesPage(pathname)) {
        return mapSpeciesPage(formattedPath, pageType);
    }

    return { page_type: pageType };
};
